import type * as PageTree from "fumadocs-core/page-tree";
import { BookOpen, Code, Map, Puzzle, Terminal } from "lucide-react";
import { createElement } from "react";

export type NavPage = string;
export type NavGroup = {
  group: string;
  expanded?: boolean;
  pages: NavItem[];
};
export type NavItem = NavPage | NavGroup;
const TAB_ICONS = {
  "book-open": BookOpen,
  "puzzle-piece": Puzzle,
  map: Map,
  terminal: Terminal,
  code: Code,
} as const;

export type NavTab = {
  tab: string;
  icon?: keyof typeof TAB_ICONS;
  groups: NavGroup[];
};

// Sidebar label for each content path. Built on the server from the fumadocs
// source (which pulls `node:path` and must stay out of the client bundle) and
// handed to `buildPageTree` as plain data, so the tree itself is built on the
// client with real icon elements and no server-only import.
export type NavTitles = Record<NavPage, string>;

export const NAVIGATION: NavTab[] = [
  {
    tab: "Documentation",
    icon: "book-open",
    groups: [
      {
        group: "Get Started",
        pages: [
          "introduction",
          "workflow",
          {
            group: "Tracing",
            expanded: true,
            pages: [
              "get-started/capture-first-trace",
              "get-started/analyze-traces",
              "get-started/signals",
            ],
          },
          {
            group: "Gateway",
            expanded: true,
            pages: [
              "get-started/record-first-call",
              "get-started/train-and-deploy",
            ],
          },
          "get-started/compare-models-auto-evals",
        ],
      },
      {
        group: "Gateway",
        pages: [
          "platform/gateway/overview",
          "platform/gateway/integrate",
          {
            group: "Tasks",
            expanded: false,
            pages: [
              "platform/gateway/tasks",
              "platform/gateway/create-a-task",
              "platform/gateway/prompt-versions",
            ],
          },
          "platform/gateway/metrics-explorer",
          "platform/gateway/inference-viewer",
        ],
      },
      {
        group: "Eval",
        pages: [
          "platform/eval/overview",
          {
            group: "AutoEvals",
            expanded: false,
            pages: [
              "platform/eval/auto-evals/run-an-auto-eval",
              "platform/eval/auto-evals/interpret-results",
            ],
          },
        ],
      },
      {
        group: "Deploy",
        pages: [
          "platform/deploy/overview",
          {
            group: "Deploying",
            expanded: false,
            pages: [
              "platform/deploy/deploy-a-model",
              "platform/deploy/call-your-deployment",
              "platform/deploy/deploy-a-hf-model",
            ],
          },
          "platform/deploy/manage-and-monitor",
          "platform/deploy/serverless-deployments",
          "platform/deploy/open-source-models",
        ],
      },
      {
        group: "Platform",
        pages: [
          "reference/api-keys",
          "reference/rate-limits",
          "reference/glossary",
        ],
      },
      {
        group: "Datasets",
        pages: [
          "platform/datasets/overview",
          "platform/datasets/build-from-traffic",
          "platform/datasets/upload-a-dataset",
          "platform/datasets/formats",
        ],
      },
      {
        group: "Train",
        pages: [
          "platform/train/overview",
          "platform/train/choose-a-recipe",
          {
            group: "Training Runs",
            expanded: true,
            pages: [
              "platform/train/launch-a-run",
              "platform/train/mid-training-evals",
              "platform/train/after-training",
            ],
          },
          "platform/train/autotrainer",
          "platform/train/troubleshooting",
        ],
      },
    ],
  },
  {
    tab: "Integrations",
    icon: "puzzle-piece",
    groups: [
      {
        group: "Integrations",
        pages: [
          "integrations/overview",
          "integrations/install-with-ai",
          "integrations/mcp-server",
        ],
      },
      {
        group: "Traces",
        expanded: true,
        pages: [
          "integrations/traces/overview",
          "integrations/traces/quickstart",
          "integrations/traces/agent-identity",
          "integrations/traces/manual-spans",
          {
            group: "Providers",
            expanded: true,
            pages: [
              "integrations/traces/openai",
              "integrations/traces/anthropic",
              "integrations/traces/ai-sdk",
            ],
          },
          {
            group: "Frameworks",
            expanded: true,
            pages: [
              "integrations/traces/langchain",
              "integrations/traces/langgraph",
              "integrations/traces/langfuse",
              "integrations/traces/langsmith",
              "integrations/traces/pydantic-ai",
            ],
          },
          {
            group: "Agent Frameworks",
            expanded: true,
            pages: [
              "integrations/traces/openai-agents",
              "integrations/traces/livekit-agents",
              "integrations/traces/elevenlabs",
              "integrations/traces/eve",
              "integrations/traces/pi-agent",
              "integrations/traces/pi-ai",
              "integrations/traces/cursor-sdk",
              "integrations/traces/claude-agent-sdk",
              "integrations/traces/claude-code-sdk",
            ],
          },
          {
            group: "AI Tools",
            expanded: true,
            pages: ["integrations/traces/opencode"],
          },
          {
            group: "Examples",
            expanded: true,
            pages: [
              "integrations/traces/examples",
              "integrations/traces/production-agent-example",
            ],
          },
          {
            group: "Reference",
            expanded: false,
            pages: [
              "integrations/traces/attributes",
              "integrations/traces/handle-api",
            ],
          },
          "integrations/traces/troubleshooting",
        ],
      },
      {
        group: "Gateway",
        pages: [
          "integrations/gateway/overview",
          "integrations/gateway/quickstart",
          "integrations/gateway/bifrost",
          {
            group: "Model providers",
            expanded: true,
            pages: [
              "integrations/model-providers/openai",
              "integrations/model-providers/anthropic",
              "integrations/model-providers/vertex-ai",
              "integrations/model-providers/amazon-bedrock",
              "integrations/model-providers/google-gemini",
              "integrations/model-providers/openrouter",
              "integrations/model-providers/cerebras",
              "integrations/model-providers/groq",
            ],
          },
          {
            group: "Frameworks",
            expanded: true,
            pages: [
              "integrations/frameworks/langchain",
              "integrations/frameworks/vercel-ai-sdk",
            ],
          },
          {
            group: "Agent Platforms",
            expanded: true,
            pages: ["integrations/agent-platforms/elevenlabs"],
          },
        ],
      },
    ],
  },
  {
    tab: "Guides",
    icon: "map",
    groups: [
      {
        group: "Guides",
        pages: [
          "guides/overview",
          "guides/optimize-an-agent-end-to-end",
          "guides/try-halo-demo-repo",
          "guides/halo-desktop-app",
          "guides/measure-agent-quality",
          "guides/find-a-better-model-with-auto-evals",
          "guides/privileged-model-access",
        ],
      },
    ],
  },
  {
    tab: "SDK & CLI",
    icon: "terminal",
    groups: [
      { group: "Get Started", pages: ["cli/overview", "cli/authentication"] },
      {
        group: "Command Reference",
        pages: [
          "cli/instrument",
          "cli/update",
          "cli/team",
          "cli/projects",
          "cli/api-key",
          "cli/models",
          "cli/evals",
          "cli/rubric",
          "cli/compare",
          "cli/datasets",
          "cli/batch",
          "cli/training",
          "cli/training-eval",
          "cli/deployment",
          "cli/deployment-instance",
          "cli/endpoint",
          "cli/gravity",
          "cli/agent",
          "cli/task",
          "cli/signals",
          "cli/signal-alert",
          "cli/notifications",
          "cli/integration",
          "cli/slack",
          "cli/github",
          "cli/search",
          "cli/starter-template",
          "cli/upload",
          "cli/worker",
          "cli/account-deletion",
          "cli/inferences",
          "cli/traces",
          "cli/spans",
          "cli/analyze-harness",
          "cli/dashboard",
        ],
      },
    ],
  },
  {
    tab: "Inference API",
    icon: "code",
    groups: [
      {
        group: "API",
        pages: [
          "api/api-quickstart",
          "api/coding-agents",
          "api/anthropic-sdk",
          "api/structured-outputs",
          "api/function-calling",
          "api/reasoning",
          "api/vision",
          {
            group: "Async API",
            pages: [
              "api/async-inference/overview",
              "api/async-inference/webhooks/getting-started-with-webhooks",
              "api/async-inference/webhooks/quick-reference",
              "api/async-inference/batch-api",
              "api/async-inference/group",
            ],
          },
          "api/rate-limits",
          "api/data-retention",
        ],
      },
      {
        group: "Workhorse Models",
        pages: ["workhorse-models/schematron", "workhorse-models/cliptagger"],
      },
    ],
  },
];

// Pages that exist and stay routable but are not linked from the sidebar
// (mirrors their status on the Mintlify site).
export const HIDDEN_PAGES: readonly string[] = [
  "changelog/overview",
  "reference/billing",
  "guides/deploy-a-trained-model",
  "guides/deploy-a-hf-model",
  "guides/e2e-finetuning",
  "guides/html-extraction-with-schematron",
];

function resolvePage(titles: NavTitles, path: NavPage): PageTree.Item {
  const name = titles[path];
  if (name === undefined) {
    throw new Error(`Navigation page has no title: ${path}`);
  }
  // baseUrl is "/", so a content path maps directly to its URL.
  return { type: "page" as const, name, url: `/${path}` };
}

function toFolder(titles: NavTitles, group: NavGroup): PageTree.Folder {
  return {
    type: "folder" as const,
    name: group.group,
    defaultOpen: group.expanded,
    children: group.pages.map((item) => toItem(titles, item)),
  };
}

function toItem(titles: NavTitles, item: NavItem): PageTree.Node {
  return typeof item === "string"
    ? resolvePage(titles, item)
    : toFolder(titles, item);
}

function toSection(titles: NavTitles, group: NavGroup): PageTree.Node[] {
  return [
    { type: "separator" as const, name: group.group },
    ...group.pages.map((item) => toItem(titles, item)),
  ];
}

/**
 * Build the sidebar tree from the manifest. Labels come from `titles` (plain
 * serializable data resolved on the server) rather than the fumadocs source,
 * so this runs on the client with real lucide icon elements and pulls no
 * server-only module into the browser bundle.
 */
export function buildPageTree(titles: NavTitles): PageTree.Root {
  return {
    name: "Inference.net Documentation",
    children: NAVIGATION.map((tab) => {
      const Icon = tab.icon ? TAB_ICONS[tab.icon] : undefined;
      return {
        type: "folder" as const,
        name: tab.tab,
        icon: Icon ? createElement(Icon, { className: "size-4" }) : undefined,
        root: true,
        children: tab.groups.flatMap((group) => toSection(titles, group)),
      };
    }),
  };
}

/** All content paths the manifest links, in nav order (for title resolution). */
export function navPagePaths(): NavPage[] {
  const paths: NavPage[] = [];
  const walk = (items: NavItem[]): void => {
    for (const item of items) {
      if (typeof item === "string") paths.push(item);
      else walk(item.pages);
    }
  };
  for (const tab of NAVIGATION) for (const group of tab.groups) walk([group]);
  return paths;
}

function containsPage(items: NavItem[], path: NavPage): boolean {
  return items.some((item) =>
    typeof item === "string" ? item === path : containsPage(item.pages, path),
  );
}

export function navSectionForPath(path: NavPage): string | undefined {
  for (const tab of NAVIGATION) {
    for (const group of tab.groups) {
      if (containsPage(group.pages, path)) return group.group;
    }
  }
}
